import React, {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useEffect,
} from 'react';
import { useAuth } from './useAuth';

export enum HiddenFeatures {}

type AllowedUsers = string[];

const ALLOWED_USERS_MAP: Record<HiddenFeatures, AllowedUsers> = {};

const computeFeatureFlagsByUser = (
  userEmail: string
): Record<HiddenFeatures, boolean> => {
  return Object.keys(ALLOWED_USERS_MAP).reduce((acc, feature) => {
    return {
      ...acc,
      [feature]: ALLOWED_USERS_MAP[feature].includes(userEmail),
    };
  }, {} as Record<HiddenFeatures, boolean>);
};

type State = {
  features: Record<HiddenFeatures, boolean> | null;
};

type Action = {
  type: 'init_feature_flags';
  userEmail: string;
};

type Dispatch = (action: Action) => void;

type ContextType = { state: State; dispatch: Dispatch } | undefined;
const FeatureFlagsContext = createContext<ContextType>(undefined);

const actionsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'init_feature_flags': {
      const features = computeFeatureFlagsByUser(action.userEmail);

      return {
        ...state,
        features,
      };
    }
  }
};

type Props = {
  children: ReactNode;
};

export function FeatureFlagsProvider({ children }: Props) {
  const { isAuthenticated, loading, user } = useAuth();
  const initialState: State = { features: null };

  const [state, dispatch] = useReducer(actionsReducer, initialState);
  const value = { state, dispatch };

  useEffect(() => {
    if (isAuthenticated && !loading && !!user) {
      dispatch({ type: 'init_feature_flags', userEmail: user.email });
    }
  }, [isAuthenticated, loading, user]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlagsContext() {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    throw new Error(
      'useFeatureFlagsContext must be used within an FeatureFlagsContextProvider'
    );
  }

  return context;
}
